// src/pages/Usuarios/UserForm.js

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Grid,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  CircularProgress,
  Button,
  Alert,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import SaveIcon from '@mui/icons-material/Save';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';

import * as userService from '../../services/userservice';

// O componente StyledTextField foi ajustado para corrigir o problema do autocomplete.
// A regra que escondia o helperText foi removida para permitir a exibição de erros.
const StyledTextField = styled(TextField)({
  // Estilos para o label do input
  '& label.Mui-focused': {
    color: '#fff',
  },
  '& label': {
    color: '#9e9e9e',
  },
  // Estilos para a raiz do componente de input
  '& .MuiOutlinedInput-root': {
    color: '#e0e0e0',
    backgroundColor: '#3A3A3D',

    // --- CORREÇÃO PARA AUTOCOMPLETE DO NAVEGADOR ---
    '& .MuiInputBase-input:-webkit-autofill': {
      '-webkit-box-shadow': '0 0 0 100px #3A3A3D inset',
      '-webkit-text-fill-color': '#e0e0e0',
      caretColor: '#fff',
      borderRadius: 'inherit',
    },
    // --- FIM DA CORREÇÃO ---

    // Estilos para a borda do input
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#555',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#777',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#fff',
    },
    // Estilos para ícones dentro do input
    '& .MuiSvgIcon-root': {
      color: '#9e9e9e',
    },
  },
  // A regra que escondia o texto de ajuda foi removida para permitir a exibição de erros de validação.
});

export default function UserForm({ open, onClose, onUserAdded }) {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    confirmarSenha: '',
    tipo: 'Usuário',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Efeito para limpar o formulário quando o modal é fechado
  useEffect(() => {
    if (!open) {
      setFormData({
        nome: '',
        email: '',
        senha: '',
        confirmarSenha: '',
        tipo: 'Usuário',
      });
      setFieldErrors({});
      setSubmitError('');
      setIsSubmitting(false);
    }
  }, [open]);

  // Efeito para validar os campos sempre que os dados do formulário mudam
  useEffect(() => {
    const newErrors = {};

    // Validação de Email para permitir @tecnomotor.com.br e @tecnomotordi.com.br
    if (
      formData.email &&
      !formData.email.endsWith('@tecnomotor.com.br') &&
      !formData.email.endsWith('@tecnomotordi.com.br')
    ) {
      newErrors.email =
        'O email deve terminar com @tecnomotor.com.br ou @tecnomotordi.com.br';
    }

    // Validação de Senha
    if (
      formData.senha &&
      formData.confirmarSenha &&
      formData.senha !== formData.confirmarSenha
    ) {
      newErrors.confirmarSenha = 'As senhas não coincidem.';
    }

    setFieldErrors(newErrors);
  }, [formData]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError('');
    try {
      await userService.createUser(formData);
      onUserAdded();
      onClose();
    } catch (err) {
      setSubmitError(err.message || 'Ocorreu um erro desconhecido.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // O botão é desabilitado se houver campos vazios, erros de validação ou se estiver enviando.
  const isButtonDisabled =
    !formData.nome ||
    !formData.email ||
    !formData.senha ||
    !formData.confirmarSenha ||
    Object.keys(fieldErrors).length > 0 ||
    isSubmitting;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 700 },
          bgcolor: '#2c2c2f',
          color: 'white',
          boxShadow: 24,
          p: 4,
          borderRadius: 3,
        }}
      >
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 3 }}>
          Cadastrar novo usuário
        </Typography>

        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        <Box component="form" noValidate onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <InputLabel sx={{ color: '#e0e0e0' }}>Nome *</InputLabel>
              <StyledTextField
                fullWidth
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                autoComplete="name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel sx={{ color: '#e0e0e0' }}>Email *</InputLabel>
              <StyledTextField
                fullWidth
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                autoComplete="email"
                error={!!fieldErrors.email}
                helperText={fieldErrors.email}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel sx={{ color: '#e0e0e0' }}>Tipo *</InputLabel>
              <FormControl fullWidth>
                <Select
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleInputChange}
                  sx={{
                    color: '#e0e0e0',
                    bgcolor: '#3A3A3D',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' },
                    '& .MuiSvgIcon-root': { color: '#9e9e9e' },
                  }}
                >
                  <MenuItem value="Usuário">Usuário</MenuItem>
                  <MenuItem value="Administrador">Administrador</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel sx={{ color: '#e0e0e0' }}>Senha *</InputLabel>
              <StyledTextField
                fullWidth
                name="senha"
                type={showPassword ? 'text' : 'password'}
                value={formData.senha}
                onChange={handleInputChange}
                autoComplete="new-password"
                helperText="A senha deve conter letras, números e pelo menos um caractere especial."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: '#9e9e9e' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel
                sx={{ color: fieldErrors.confirmarSenha ? '#f44336' : '#e0e0e0' }}
              >
                Confirmar Senha *
              </InputLabel>
              <StyledTextField
                fullWidth
                name="confirmarSenha"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmarSenha}
                onChange={handleInputChange}
                error={!!fieldErrors.confirmarSenha}
                helperText={fieldErrors.confirmarSenha}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        sx={{ color: '#9e9e9e' }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{
                textTransform: 'none',
                fontSize: '1rem',
                color: '#e0e0e0',
                borderColor: '#757575',
                '&:hover': {
                  borderColor: '#aaa',
                  bgcolor: '#424242',
                },
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={isButtonDisabled}
              sx={{
                textTransform: 'none',
                fontSize: '1rem',
                bgcolor: '#006A93',
                '&:hover': { bgcolor: '#00587A' }, // Cor do hover alterada para um azul mais escuro
                '&:disabled': { bgcolor: '#424242', color: '#757575' },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Salvar'
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
